/*
 *   File : upsert-question.js
 *   Author : https://evoqins.com
 *   Description : upsert question component
 *   Integrations : risk/question-detail, risk/upsert-question
 *   Version : 1.2.1
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Input, RadioGroup } from "../../Components/FormElements";
import { PrimaryButton } from "../../Components/Buttons";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { APIService } from "../../Service";
import { SuccessModal } from "../../Components/Modals";

export default function UpsertQuestion() {
  const { q_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ---------------- PAGE SETTINGS ---------------- */
  useEffect(() => {
    dispatch(setPageTitle(["Manage Risk Score", q_id ? "Edit" : "Add"]));
    dispatch(setSearchEnabled(false));
    dispatch(setNavLink(["/manage-risk-score"]));
  }, [q_id]);

  /* ---------------- STATES ---------------- */
  const [loader, setLoader] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const [question, setQuestion] = useState("");
  const [questionType, setQuestionType] = useState();
  const [weightage, setWeightage] = useState("");

  const [options, setOptions] = useState([
    { option: "", weightage: "", answer_id: "" },
    { option: "", weightage: "", answer_id: "" },
    { option: "", weightage: "", answer_id: "" },
    { option: "", weightage: "", answer_id: "" },
  ]);

  /* ---------------- ERRORS ---------------- */
  const [questionError, setQuestionError] = useState("");
  const [weightageError, setWeightageError] = useState("");
  const [optionErrors, setOptionErrors] = useState([]);
  const [scoreErrors, setScoreErrors] = useState([]);

  /* ---------------- FETCH QUESTION (EDIT) ---------------- */
  useEffect(() => {
    if (q_id) {
      _fetchQuestionDetail();
    } else {
      setQuestionType("MCQ");
    }
  }, [q_id]);

  const _fetchQuestionDetail = async () => {
    try {
      const res = await APIService(true, "risk/question-detail", {
        question_id: Number(q_id),
      });

      if (res.status_code === 200 && res.data) {
        const data = res.data;

        setQuestion(data.question || "");
        setQuestionType(data.question_type || "MCQ");
        setWeightage(
          data.weight !== undefined && data.weight !== null
            ? String(data.weight)
            : "",
        );

        const mapped = Array.isArray(data.answers)
          ? data.answers.map((ans) => ({
              option: ans.label ?? "",
              weightage:
                ans.score !== undefined && ans.score !== null
                  ? String(ans.score)
                  : "",
              answer_id:
                ans.answer_id !== undefined ? String(ans.answer_id) : "",
            }))
          : [];

        if (mapped.length === 0) {
          setOptions(
            (data.question_type || "MCQ") === "MCQ"
              ? [
                  { option: "", weightage: "", answer_id: "" },
                  { option: "", weightage: "", answer_id: "" },
                  { option: "", weightage: "", answer_id: "" },
                  { option: "", weightage: "", answer_id: "" },
                ]
              : [
                  { option: "Yes", weightage: "", answer_id: "" },
                  { option: "No", weightage: "", answer_id: "" },
                ],
          );
        } else {
          setOptions(mapped);
        }
      }
    } catch (err) {
      console.error("risk/question-detail error:", err);
    }
  };

  /* ---------------- HANDLE TYPE TOGGLE ---------------- */
  useEffect(() => {
    if (!questionType) return;

    setOptionErrors([]);
    setScoreErrors([]);
    setQuestionError("");

    setOptions((prev) => {
      const copy = [...prev];

      if (questionType === "Yes/No") {
        const truncated = copy.slice(0, 2);
        while (truncated.length < 2) {
          truncated.push({ option: "", weightage: "", answer_id: "" });
        }
        truncated[0] = { ...truncated[0], option: "Yes" };
        truncated[1] = { ...truncated[1], option: "No" };
        return truncated;
      } else {
        const hasYesNo =
          copy.length === 2 &&
          copy[0]?.option === "Yes" &&
          copy[1]?.option === "No";

        if (hasYesNo) {
          return [
            { option: "", weightage: "", answer_id: "" },
            { option: "", weightage: "", answer_id: "" },
            { option: "", weightage: "", answer_id: "" },
            { option: "", weightage: "", answer_id: "" },
          ];
        } else {
          while (copy.length < 4) {
            copy.push({ option: "", weightage: "", answer_id: "" });
          }
          return copy;
        }
      }
    });
  }, [questionType]);

  /* ---------------- OPTION HANDLER ---------------- */
  const _handleOption = (value, index, field) => {
    setOptions((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });

    if (field === "option") {
      setOptionErrors((prev) => {
        const newErrors = [...prev];
        newErrors[index] = "";
        return newErrors;
      });
    } else if (field === "weightage") {
      setScoreErrors((prev) => {
        const newErrors = [...prev];
        if (value !== "" && Number(value) > 10) {
          newErrors[index] = "Score cannot exceed 10";
        } else {
          newErrors[index] = "";
        }
        return newErrors;
      });
    }
  };

  /* ---------------- ENTER KEY SUBMIT ---------------- */
  const _handleKeyPress = useCallback(
    (e) => {
      if (e?.key === "Enter") {
        _validateForm();
      }
    },
    [question, weightage, options],
  );

  /* ---------------- VALIDATION ---------------- */
  const _validateForm = () => {
    let hasError = false;

    // Question validation
    if (!question.trim()) {
      setQuestionError("Enter your question");
      hasError = true;
    } else {
      setQuestionError("");
    }

    // Weightage validation
    if (!weightage.trim()) {
      setWeightageError("Weightage is required");
      hasError = true;
    } else {
      setWeightageError("");
    }

    // Options validation
    const newOptionErrors = [];
    const newScoreErrors = [];

    for (let i = 0; i < options.length; i++) {
      // For Yes/No type, don't validate option text (it's auto-filled and disabled)
      if (questionType !== "Yes/No") {
        if (!String(options[i].option || "").trim()) {
          newOptionErrors[i] = "Option required";
          hasError = true;
        } else {
          newOptionErrors[i] = "";
        }
      } else {
        newOptionErrors[i] = "";
      }

      // Always validate score for all types
      if (String(options[i].weightage).trim() === "") {
        newScoreErrors[i] = "Score required";
        hasError = true;
      } else if (Number(options[i].weightage) > 10) {
        newScoreErrors[i] = "Score cannot exceed 10";
        hasError = true;
      } else {
        newScoreErrors[i] = "";
      }
    }

    setOptionErrors(newOptionErrors);
    setScoreErrors(newScoreErrors);

    if (hasError) return;

    _addOrUpdateQuestion();
  };

  /* ---------------- ADD / EDIT API ---------------- */
  const _addOrUpdateQuestion = async () => {
    try {
      setLoader(true);

      const payload = {
        type: q_id ? 2 : 1,
        question_id: q_id ? String(q_id) : null,
        question: question,
        question_type: questionType || "MCQ",
        weight: Number(weightage),
        answers: options.map((opt) => ({
          answer_id: opt.answer_id ? String(opt.answer_id) : null,
          answer: opt.option,
          score: Number(opt.weightage),
        })),
      };
      console.log(payload);

      const res = await APIService(true, "risk/upsert-question", payload);

      if (res.status_code === 200) {
        setSuccessModal(true);
      } else {
        console.error("upsert-question failed", res);
      }
    } catch (err) {
      console.error("risk/upsert-question error:", err);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column p-24 e-glass-card rounded-32 bg-transparent">
          <div className="row">
            <div className="col-lg-6 d-flex flex-column">
              {/* Question */}
              <Input
                id="question"
                type="text"
                label="Question"
                placeholder="Write a question"
                require={true}
                value={question}
                error={questionError}
                className="w-100 px-3 py-10"
                handleChange={(v) => {
                  setQuestion(v);
                  setQuestionError("");
                }}
                onKeyPress={_handleKeyPress}
                autoFocus
              />

              {/* Weightage */}
              <Input
                id="weightage"
                type="number"
                label="Weightage"
                placeholder="Add Weightage"
                require={true}
                value={weightage}
                error={weightageError}
                className="w-100 px-3 py-10"
                handleChange={(v) => {
                  setWeightage(v);
                  setWeightageError("");
                }}
                onKeyPress={_handleKeyPress}
              />

              <RadioGroup
                label="Question Type"
                require={true}
                defaultValue={"MCQ"}
                options={[
                  { label: "MCQ", value: "MCQ" },
                  { label: "Yes/No", value: "Yes/No" },
                ]}
                value={questionType}
                onChange={(val) => {
                  setQuestionType(val);
                }}
              />

              {/* Options */}
              <div className="e-glass-card rounded-16 p-3 w-100">
                <h5 className="fs-16 fw-500 text-eerie-black mb-0">
                  Options & Weightage
                </h5>

                <div className="d-flex flex-column pt-20">
                  {options.map((opt, index) => (
                    <div className="row" key={index}>
                      <div className="col-lg-8">
                        <Input
                          id={`opt-${index}`}
                          type="text"
                          label={`Option ${index + 1}`}
                          placeholder="Enter option name"
                          require={true}
                          value={opt.option}
                          error={optionErrors[index] || ""}
                          className="w-100 px-3 py-10"
                          handleChange={(v) =>
                            _handleOption(v, index, "option")
                          }
                          onKeyPress={_handleKeyPress}
                          disabled={questionType === "Yes/No"}
                        />
                      </div>

                      <div className="col-lg-4">
                        <Input
                          id={`opt-score-${index}`}
                          type="number"
                          label="Score"
                          placeholder="Enter score"
                          require={true}
                          value={opt.weightage}
                          error={scoreErrors[index] || ""}
                          className="w-100 px-3 py-10"
                          handleChange={(v) =>
                            _handleOption(v, index, "weightage")
                          }
                          onKeyPress={_handleKeyPress}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Add / Delete options */}
                  {questionType === "MCQ" && (
                    <div className="row">
                      <div className="col-lg-5">
                        <PrimaryButton
                          ClassName="bg-transparent fs-14 lh-22 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
                          name="Add new option"
                          onPress={() =>
                            setOptions((prev) => [
                              ...prev,
                              { option: "", weightage: "", answer_id: "" },
                            ])
                          }
                        />
                      </div>

                      {options.length > 4 && (
                        <div className="offset-lg-2 col-lg-5">
                          <PrimaryButton
                            ClassName="fw-500 fs-14 w-100 bg-transparent text-pure-red px-0 p-0 text-end"
                            name="Delete new Option"
                            onPress={() =>
                              setOptions((prev) => prev.slice(0, -1))
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="row row-cols-2 pt-24">
                <div className="col">
                  <PrimaryButton
                    name="Cancel"
                    ClassName="rounded-pill bg-transparent border-oxford-blue lh-24 py-3 text-oxford-blue w-100 fs-16 fw-500"
                    onPress={() => navigate("/manage-risk-score")}
                  />
                </div>
                <div className="col">
                  <PrimaryButton
                    loading={loader}
                    name={q_id ? "Save changes" : "Add"}
                    ClassName="rounded-pill bg-oxford-blue lh-24 py-3 text-white w-100 fs-16 fw-500"
                    onPress={_validateForm}
                    disabled={loader}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SuccessModal
        title="Successful"
        message={
          q_id
            ? "Your Question is edited successfully "
            : "Your Question is added successfully"
        }
        open={successModal}
        onConfirm={() => {
          setSuccessModal(false);
          navigate("/manage-risk-score");
        }}
      />
    </div>
  );
}